import { useRef, useCallback, useEffect } from 'react';

/**
 * useAutoSave — debounced auto-save for inline card editors.
 *
 * Used by DayBuilder, ScheduleBuilder, DisciplineRules, etc.
 * The callback ref ensures the timeout always calls the latest onSave,
 * even if the parent re-renders between the edit and the debounce firing.
 *
 * @param {function} onSave - called with whatever args are passed to save() after debounce
 * @param {number} delay - debounce delay in ms (default 300)
 * @returns {{ save: function, cancel: function }}
 */
export function useAutoSave(onSave, delay = 300) {
  const timeout = useRef();
  const callbackRef = useRef(onSave);
  callbackRef.current = onSave;

  useEffect(() => {
    return () => { if (timeout.current) clearTimeout(timeout.current); };
  }, []);

  const save = useCallback((...args) => {
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => {
      callbackRef.current(...args);
    }, delay);
  }, [delay]);

  const cancel = useCallback(() => {
    if (timeout.current) clearTimeout(timeout.current);
  }, []);

  return { save, cancel };
}
